'use client'

import * as React from 'react'

import type { DownloadPageContent, SharedContent, ToolSlug } from '../../../../lib/content/types'
import { toolDefinitionsBySlug } from '../../../../lib/content/tool-definitions'
import { SparkleStar } from '../../../../components/sparkle-star'
import { SectionContainer } from './primitives'
import { ToolIcon } from './tool-icon'

/** Shield-with-check glyph used by the sidebar privacy note. */
function ShieldCheckIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      <path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  )
}

/**
 * Interactive "app window" mock under the hero. Sidebar rows are clickable
 * tool switchers; the canvas updates to reflect the selected tool.
 */
export function ProductStageMock({
  mock,
  tools,
}: {
  mock: SharedContent['mock']
  tools: DownloadPageContent['tools']['items']
}) {
  const [selectedSlug, setSelectedSlug] = React.useState<ToolSlug>(tools[0].slug)
  const selectedTool = tools.find(tool => tool.slug === selectedSlug) ?? tools[0]
  const selectedAccent = toolDefinitionsBySlug[selectedSlug]

  return (
    <section className="pb-2 pt-[62px]">
      <SectionContainer className="max-w-[1040px]">
        <div className="relative">
          {/* Floating sparkles */}
          <SparkleStar className="animate-saiFloat absolute -left-[18px] top-[34px] size-[38px] text-[#F9A8D4]" />
          <SparkleStar className="animate-saiFloat2 absolute -right-2 -top-[22px] size-[26px] text-[#FFD966]" />

          {/* Pastel stage */}
          <div className="rounded-[32px] bg-[linear-gradient(135deg,#F4EEFF_0%,#FCE7F3_100%)] p-4 shadow-[0_44px_100px_rgba(83,35,211,0.18)] sm:p-[38px]">
            {/* App window */}
            <div className="overflow-hidden rounded-[16px] border border-[rgba(19,19,21,0.06)] bg-white text-left shadow-[0_26px_64px_rgba(19,19,21,0.2)]">
              {/* Title bar */}
              <div className="flex items-center gap-2 border-b border-[rgba(19,19,21,0.06)] bg-[#FBFBFC] px-4 py-[13px]">
                <span className="size-[11px] rounded-full bg-[#FF5F57]" aria-hidden />
                <span className="size-[11px] rounded-full bg-[#FEBC2E]" aria-hidden />
                <span className="size-[11px] rounded-full bg-[#28C840]" aria-hidden />
                <span className="ml-3 text-[13px] font-medium text-[#374151]">Studio Desktop</span>
                <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-sd-mint px-[9px] py-[3px] font-mono text-[11px] font-medium text-sd-green">
                  <span className="size-1.5 rounded-full bg-sd-green" aria-hidden />
                  {mock.status}
                </span>
              </div>

              {/* Body */}
              <div className="grid min-h-[400px] grid-cols-1 md:grid-cols-[240px_1fr]">
                {/* Free-models sidebar */}
                <div className="bg-[#FBFAFC] px-[14px] py-[18px] md:border-r md:border-[rgba(19,19,21,0.06)]">
                  <p className="px-2 pb-[10px] font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-sd-slate-3">
                    {mock.freeModels}
                  </p>
                  <ul className="flex flex-col">
                    {tools.map((tool, index) => {
                      const accent = toolDefinitionsBySlug[tool.slug]
                      const isSelected = tool.slug === selectedSlug
                      return (
                        <li key={tool.slug} style={{ marginTop: index === 0 ? 0 : 4 }}>
                          <button
                            type="button"
                            onClick={() => setSelectedSlug(tool.slug)}
                            className={`flex w-full items-center gap-[10px] rounded-[10px] border border-transparent p-[10px] text-left transition-colors${!isSelected ? ' hover:bg-[rgba(19,19,21,0.04)]' : ''}`}
                            style={isSelected ? { backgroundColor: accent.accentBg } : undefined}
                            aria-pressed={isSelected}
                          >
                            <span
                              className="flex size-[30px] shrink-0 items-center justify-center rounded-lg [&_svg]:size-[15px]"
                              style={{ background: accent.accentBg, color: accent.accent }}
                            >
                              <ToolIcon slug={tool.slug} />
                            </span>
                            <div>
                              <div
                                className="text-[13px] font-semibold"
                                style={{ color: isSelected ? accent.accent : '#374151' }}
                              >
                                {tool.name}
                              </div>
                              <div
                                className={`mt-0.5 text-[10px] font-medium${!isSelected ? ' text-sd-slate-3' : ''}`}
                                style={isSelected ? { color: accent.accent } : undefined}
                              >
                                {isSelected ? mock.selected : mock.free}
                              </div>
                            </div>
                          </button>
                        </li>
                      )
                    })}
                  </ul>

                  <div className="mt-[14px] flex items-center gap-[9px] rounded-[12px] border border-[#D5F0DF] bg-[#F1FAF4] px-3 py-[11px]">
                    <span className="flex size-[26px] shrink-0 items-center justify-center rounded-lg bg-sd-mint text-sd-green-deep [&_svg]:size-[14px]">
                      <ShieldCheckIcon />
                    </span>
                    <span className="text-[11px] font-medium leading-[1.35] text-sd-green-deep">
                      {mock.note}
                    </span>
                  </div>
                  <p className="mt-[10px] text-center font-mono text-[11px] font-medium text-sd-slate-3">
                    {mock.noCard}
                  </p>
                </div>

                {/* Canvas — updates to reflect the selected tool */}
                <div
                  className="relative flex flex-col items-center justify-center bg-[length:18px_18px] px-[34px] pb-[92px] pt-[34px]"
                  style={{
                    backgroundImage: `radial-gradient(${selectedAccent.accentBg} 1px, transparent 1px)`,
                  }}
                >
                  <div className="flex flex-col items-center gap-3 text-center">
                    {/* Tool mode pill */}
                    <span
                      className="flex items-center gap-1.5 rounded-full px-[10px] py-[5px] text-[11px] font-semibold transition-colors [&_svg]:size-[11px]"
                      style={{
                        background: selectedAccent.accentBg,
                        color: selectedAccent.accent,
                      }}
                    >
                      <ToolIcon slug={selectedSlug} />
                      {selectedTool.name}
                    </span>

                    {/* Tool description */}
                    <p className="max-w-[300px] text-center text-[14px] leading-[1.45] text-sd-ink">
                      {selectedTool.desc}
                    </p>
                  </div>

                  <div className="absolute inset-x-[26px] bottom-6 flex items-center gap-[10px] rounded-[12px] border border-[rgba(19,19,21,0.1)] bg-white px-[13px] py-[11px] shadow-[0_10px_28px_rgba(19,19,21,0.08)]">
                    <span className="flex-1 text-[13px] text-sd-slate-3">{mock.prompt}</span>
                    <span
                      className="inline-flex items-center gap-[7px] whitespace-nowrap rounded-lg px-[14px] py-2 text-[12px] font-semibold text-white transition-colors"
                      style={{ backgroundColor: selectedAccent.accent }}
                    >
                      {mock.generate}
                      <SparkleStar className="size-[13px]" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </SectionContainer>
    </section>
  )
}
